import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { logger } from "@/lib/logger";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, FileText, Star, Share2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import {
  DadosPessoaisCard, ConjugeCard, EnderecoCard, FamiliaSocialCard, RendaCard,
  DocumentosCard, AnalisePanel, HistoricoCard, StatusHeaderBadges,
} from "./detalhe/DetalheSections";
import TransferirTitularidadeCard from "./detalhe/TransferirTitularidadeCard";
import DocumentosChecklist from "./detalhe/DocumentosChecklist";
import DossieHistoryCard from "./detalhe/DossieHistoryCard";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const SHARE_VALIDITIES = [
  { v: "1h", label: "1 hora" },
  { v: "24h", label: "24 horas" },
  { v: "7d", label: "7 dias" },
  { v: "30d", label: "30 dias" },
];

export default function DetalheInscricaoPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [insc, setInsc] = useState(null);
  const [novoStatus, setNovoStatus] = useState("");
  const [parecer, setParecer] = useState("");
  const isAdmin = user?.role === "super_admin";
  const userPerms = user?.permissoes || [];
  const canHomologar = isAdmin || userPerms.includes("inscricoes.homologar");
  const canDossie = isAdmin || userPerms.includes("inscricoes.view");

  // Dossiê compartilhável
  const [shareOpen, setShareOpen] = useState(false);
  const [shareValidity, setShareValidity] = useState("24h");
  const [shareLink, setShareLink] = useState("");
  const [shareExp, setShareExp] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/inscricoes/${id}`);
      setInsc(r.data);
      setNovoStatus(r.data.status);
      setParecer(r.data.parecer || "");
    } catch (e) {
      logger.error("Falha ao carregar inscrição", e);
      toast.error("Falha ao carregar");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const salvarStatus = async () => {
    try {
      await api.post(`/inscricoes/${id}/status`, { status: novoStatus, parecer });
      toast.success("Status atualizado!");
      load();
    } catch (e) {
      logger.error("Falha ao salvar status", e);
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const toggleFavorite = async () => {
    try {
      const r = await api.post(`/inscricoes/${id}/favorite`);
      setInsc((prev) => prev ? { ...prev, is_favorite: r.data.is_favorite } : prev);
      toast.success(r.data.is_favorite ? "★ Favoritada" : "☆ Removida dos favoritos");
    } catch (e) {
      logger.error("Falha ao favoritar", e);
      toast.error(e.response?.data?.detail || "Falha");
    }
  };

  const gerarShareLink = async () => {
    setShareLoading(true);
    setShareLink(""); setShareCopied(false);
    try {
      const r = await api.post(`/inscricoes/${id}/dossie/share`, { validity: shareValidity });
      // Preenche com URL absoluta para funcionar fora do app
      const link = r.data.link.startsWith("http") ? r.data.link : `${window.location.origin}${r.data.link}`;
      setShareLink(link);
      setShareExp(r.data.exp);
    } catch (e) {
      logger.error("Falha ao gerar link", e);
      toast.error(e.response?.data?.detail || "Falha ao gerar link");
    } finally { setShareLoading(false); }
  };

  const copyShareLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setShareCopied(true);
      toast.success("Link copiado!");
      setTimeout(() => setShareCopied(false), 2500);
    } catch {
      toast.error("Falha ao copiar — selecione e copie manualmente");
    }
  };

  if (!insc) return <div>Carregando...</div>;

  return (
    <div className="space-y-6" data-testid="detalhe-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Button variant="ghost" size="sm" onClick={() => nav(-1)}>
            <ArrowLeft className="w-4 h-4 mr-1" />Voltar
          </Button>
          <h1 className="text-2xl font-black text-[#083CA6] mt-2">{insc.nome_completo}</h1>
          <StatusHeaderBadges insc={insc} />
        </div>
        <Button variant="outline" onClick={() => window.open(`${API}/inscricoes/${id}/comprovante`, "_blank")} data-testid="btn-comprovante">
          <Download className="w-4 h-4 mr-2" />Comprovante PDF
        </Button>
        {canDossie && (
          <Button
            variant="outline"
            className={insc.is_favorite
              ? "border-amber-400 text-amber-600 hover:bg-amber-50"
              : "text-slate-600 hover:text-amber-600"}
            onClick={toggleFavorite}
            data-testid="btn-favorite"
            title={insc.is_favorite ? "Desfavoritar" : "Favoritar"}
          >
            <Star className={`w-4 h-4 mr-2 ${insc.is_favorite ? "fill-current" : ""}`} />
            {insc.is_favorite ? "Favoritada" : "Favoritar"}
          </Button>
        )}
        {canDossie && (
          <Button
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={() => window.open(`${API}/inscricoes/${id}/dossie`, "_blank")}
            data-testid="btn-dossie"
            title="Dossiê completo: ficha + anexos em um único PDF"
          >
            <FileText className="w-4 h-4 mr-2" />Gerar Dossiê
          </Button>
        )}
        {canDossie && (
          <Button
            variant="outline"
            className="border-sky-400 text-sky-700 hover:bg-sky-50"
            onClick={() => { setShareOpen(true); setShareLink(""); }}
            data-testid="btn-share-dossie"
            title="Gerar link temporário para enviar o dossiê a um auditor"
          >
            <Share2 className="w-4 h-4 mr-2" />Compartilhar
          </Button>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <DadosPessoaisCard insc={insc} />
          <ConjugeCard insc={insc} />
          <EnderecoCard insc={insc} />
          <FamiliaSocialCard insc={insc} />
          <RendaCard insc={insc} />
          <DocumentosCard insc={insc} id={id} />
        </div>

        <div className="space-y-4">
          <DocumentosChecklist inscricao={insc} />
          {canHomologar && (
            <AnalisePanel
              novoStatus={novoStatus} setNovoStatus={setNovoStatus}
              parecer={parecer} setParecer={setParecer}
              onSave={salvarStatus}
            />
          )}
          {isAdmin && (
            <TransferirTitularidadeCard insc={insc} onDone={load} />
          )}
          {canDossie && <DossieHistoryCard inscricaoId={id} />}
          <HistoricoCard historico={insc.historico} />
        </div>
      </div>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-lg" data-testid="dialog-share-dossie">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sky-700">
              <Share2 className="w-5 h-5" /> Compartilhar Dossiê por link
            </DialogTitle>
            <DialogDescription>
              Gera um link temporário assinado para enviar o dossiê a um auditor externo —
              sem precisar de login. Cada acesso é registrado em auditoria.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Validade do link</label>
              <Select value={shareValidity} onValueChange={setShareValidity}>
                <SelectTrigger data-testid="select-share-validity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SHARE_VALIDITIES.map((o) =>
                    <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            {!shareLink && (
              <Button
                className="w-full bg-sky-600 hover:bg-sky-700"
                onClick={gerarShareLink}
                disabled={shareLoading}
                data-testid="btn-share-generate"
              >
                {shareLoading ? "Gerando..." : "Gerar link"}
              </Button>
            )}
            {shareLink && (
              <div className="space-y-2">
                <div className="bg-slate-50 border rounded p-2 flex items-start gap-2">
                  <input
                    readOnly
                    value={shareLink}
                    className="w-full bg-transparent text-xs font-mono outline-none"
                    data-testid="input-share-link"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={copyShareLink}
                    className="shrink-0"
                    data-testid="btn-share-copy"
                  >
                    {shareCopied
                      ? <><Check className="w-4 h-4 mr-1" />Copiado</>
                      : <><Copy className="w-4 h-4 mr-1" />Copiar</>}
                  </Button>
                </div>
                <div className="text-xs text-slate-500">
                  Expira em: <strong>{shareExp ? new Date(shareExp).toLocaleString("pt-BR") : "—"}</strong>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setShareLink(""); setShareCopied(false); }} className="text-xs">
                  Gerar outro link
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
